/**
 * Repository List Component
 *
 * Displays list of connected code repositories with scanning controls
 *
 * Features:
 * - Repository status badges
 * - Last scan information
 * - Quick actions (scan, view results, disconnect)
 * - Responsive grid layout
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Button, Spinner, Alert } from 'flowbite-react';
import { HiCode, HiRefresh, HiEye, HiTrash } from 'react-icons/hi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface Repository {
  Repository_ID: string;
  Repository_Name: string;
  Repository_URL: string;
  Provider: string; // 'github' | 'gitlab' | 'bitbucket'
  Status: string; // 'connected' | 'scanning' | 'error'
  Last_Scan_Date?: string;
  Total_Issues?: number;
  Critical_Issues?: number;
  Company_ID: string;
  Client_ID: string;
}

interface RepositoryListProps {
  clientId: string;
  companyId: string;
  onViewScans?: (repositoryId: string) => void;
  onRefresh?: () => void;
}

export const RepositoryList: React.FC<RepositoryListProps> = ({
  clientId,
  companyId,
  onViewScans,
  onRefresh
}) => {
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanningRepos, setScanningRepos] = useState<Set<string>>(new Set());

  const fetchRepositories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await Make_Authorized_API_Call<Repository>(
        API_ENDPOINTS.REPOSITORIES.LIST,
        'GET',
        undefined,
        { params: { Client_ID: clientId, Company_ID: companyId } }
      );

      if (isSuccessResponse(response)) {
        setRepositories(response.data?.Records || []);
      } else {
        throw new Error(response.message || 'API returned unsuccessful response');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load repositories';
      console.error('[RepositoryList] Error fetching repositories:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [clientId, companyId]);

  useEffect(() => {
    if (clientId && companyId) {
      fetchRepositories();
    }
  }, [clientId, companyId, fetchRepositories]);

  const handleScanRepository = async (repositoryId: string) => {
    try {
      setScanningRepos(prev => new Set(prev).add(repositoryId));

      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.REPOSITORIES.SCAN,
        'POST',
        {
          Repository_ID: repositoryId,
          Client_ID: clientId,
          Company_ID: companyId,
        }
      );

      if (!isSuccessResponse(response)) {
        throw new Error('Failed to start repository scan');
      }

      // Refresh list after initiating scan
      await fetchRepositories();
      onRefresh?.();
    } catch (err) {
      console.error('[RepositoryList] Error scanning repository:', err);
      alert('Failed to start repository scan');
    } finally {
      setScanningRepos(prev => {
        const newSet = new Set(prev);
        newSet.delete(repositoryId);
        return newSet;
      });
    }
  };

  const handleDisconnect = async (repositoryId: string, repositoryName: string) => {
    if (!window.confirm(`Are you sure you want to disconnect "${repositoryName}"?`)) {
      return;
    }

    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.REPOSITORIES.DELETE,
        'DELETE',
        undefined,
        { params: { Repository_ID: repositoryId, Client_ID: clientId, Company_ID: companyId } }
      );

      if (!isSuccessResponse(response)) {
        throw new Error('Failed to disconnect repository');
      }

      await fetchRepositories();
      onRefresh?.();
    } catch (err) {
      console.error('[RepositoryList] Error disconnecting repository:', err);
      alert('Failed to disconnect repository');
    }
  };

  const getProviderBadgeColor = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'github':
        return 'dark';
      case 'gitlab':
        return 'warning';
      case 'bitbucket':
        return 'info';
      default:
        return 'gray';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'connected':
        return 'success';
      case 'scanning':
        return 'warning';
      case 'error':
        return 'failure';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure">
        <div>
          <p className="font-semibold">Error loading repositories</p>
          <p className="text-sm mt-1">{error}</p>
          <Button
            size="xs"
            color="light"
            className="mt-2"
            onClick={fetchRepositories}
          >
            Retry
          </Button>
        </div>
      </Alert>
    );
  }

  if (repositories.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <HiCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No repositories connected</p>
          <p className="text-sm text-gray-400">
            Click "Connect Repository" to add your first repository
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {repositories.map((repo) => (
        <Card key={repo.Repository_ID}>
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 truncate">
                  {repo.Repository_Name}
                </h3>
                <p className="text-xs text-gray-500 truncate font-mono mt-1">
                  {repo.Repository_URL}
                </p>
              </div>
              <HiCode className="w-6 h-6 text-gray-400 ml-2 flex-shrink-0" />
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              <Badge color={getProviderBadgeColor(repo.Provider)}>
                {repo.Provider.toUpperCase()}
              </Badge>
              <Badge color={getStatusBadgeColor(repo.Status)}>
                {repo.Status.toUpperCase()}
              </Badge>
            </div>

            {/* Scan Info */}
            {repo.Last_Scan_Date && (
              <div className="mb-3 p-2 bg-gray-50 rounded">
                <p className="text-xs text-gray-600">
                  Last scan: {new Date(repo.Last_Scan_Date).toLocaleDateString()}
                </p>
                {(repo.Total_Issues !== undefined || repo.Critical_Issues !== undefined) && (
                  <div className="flex gap-3 mt-1">
                    {repo.Total_Issues !== undefined && (
                      <p className="text-xs text-gray-600">
                        Total: <span className="font-semibold">{repo.Total_Issues}</span>
                      </p>
                    )}
                    {repo.Critical_Issues !== undefined && repo.Critical_Issues > 0 && (
                      <p className="text-xs text-red-600">
                        Critical: <span className="font-semibold">{repo.Critical_Issues}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 mt-auto">
              <Button
                size="sm"
                color="blue"
                onClick={() => handleScanRepository(repo.Repository_ID)}
                disabled={scanningRepos.has(repo.Repository_ID) || repo.Status === 'scanning'}
              >
                <HiRefresh className="w-4 h-4 mr-2" />
                {scanningRepos.has(repo.Repository_ID) || repo.Status === 'scanning' ? 'Scanning...' : 'Scan Now'}
              </Button>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  color="light"
                  className="flex-1"
                  onClick={() => onViewScans?.(repo.Repository_ID)}
                >
                  <HiEye className="w-4 h-4 mr-2" />
                  View Scans
                </Button>
                <Button
                  size="sm"
                  color="failure"
                  onClick={() => handleDisconnect(repo.Repository_ID, repo.Repository_Name)}
                >
                  <HiTrash className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
